/* eslint-disable no-param-reassign */
import * as d3 from 'd3';
import { colorScale } from './utils';

export const HIERARCHICAL_DONUT = 'hierarchical_donut';

// calc types for which the hierarchy of a list can be resolved
export const hierarchicalCalcs = ['COUNT_ABSOLUT_LIST', 'COUNT_SEPARE_LIST'];

/**
 * @param {*} labels labels of an associated list ({ element_id, label, parent })
 * @returns true if at least one element of the list has a parent
 */
export const isHierarchicalLabels = (labels = []) => (Array.isArray(labels)
  ? labels.some((l) => l?.parent?.element_id || l?.parent_id)
  : false);

/**
 * Transform a flat data array into the nodes of a tree :
 * - every ancestor of a value is added to the data (even when no entity is directly
 *   attached to it) so the donut can start from the roots of the list
 * - the value of a node is its own value + the values of all its descendants
 * - nodes are ordered in tree order (a parent is always followed by its children)
 * - colors are computed by group of siblings so each level of the donut stays readable
 *
 * @param {*} items flat data ({ name, value, ... }) as generated for a pie
 * @param {*} labels labels of the associated list ({ element_id, label, parent })
 * @param {*} getPrevColor (id) => color already chosen for this item
 * @returns the nodes of the tree, flat, enriched with
 *          elementId / parentId / parentName / ownValue / depth / hasChildren
 */
export const buildHierarchicalData = (items = [], labels = [], getPrevColor = () => null) => {
  if (!items?.length) return [];
  const labelById = {};
  const labelByName = {};
  labels.forEach((l) => {
    if (l?.element_id) labelById[l.element_id] = l;
    if (l?.label && !labelByName[l.label]) labelByName[l.label] = l;
  });

  const nodes = [];
  const nodeByKey = {};

  const addNode = (node) => {
    nodeByKey[node.key] = node;
    nodes.push(node);
    return node;
  };

  // 1. ONE NODE PER DATA ITEM
  items.forEach((item) => {
    const labelInfo = labelByName[item.name];
    const key = labelInfo?.element_id || `name:${item.name}`;
    if (nodeByKey[key]) {
      nodeByKey[key].ownValue += item.value || 0;
      return;
    }
    addNode({
      key,
      item,
      elementId: labelInfo?.element_id || null,
      parentKey: labelInfo?.parent?.element_id || null,
      name: item.name,
      ownValue: item.value || 0,
    });
  });

  // 2. ADD THE MISSING ANCESTORS (LISTS WHERE ONLY THE LEAVES ARE USED)
  const toResolve = [...nodes];
  while (toResolve.length > 0) {
    const node = toResolve.pop();
    const parentLabel = node.parentKey && labelById[node.parentKey];
    if (parentLabel && !nodeByKey[parentLabel.element_id]) {
      toResolve.push(addNode({
        key: parentLabel.element_id,
        item: {
          ...node.item, name: parentLabel.label, id: parentLabel.label, value: 0,
        },
        elementId: parentLabel.element_id,
        parentKey: parentLabel.parent?.element_id || null,
        name: parentLabel.label,
        ownValue: 0,
      }));
    }
  }

  // 3. LINK THE NODES TOGETHER (A PARENT OUTSIDE OF THE DATA STAYS A ROOT)
  nodes.forEach((node) => {
    node.parent = (node.parentKey && nodeByKey[node.parentKey]) || null;
    node.children = [];
  });
  nodes.forEach((node) => {
    if (node.parent) node.parent.children.push(node);
  });

  // 4. DEPTH FROM THE ROOTS, A NODE IS ONLY VISITED ONCE
  const preOrder = [];
  const walk = (node, depth) => {
    if (node.depth !== undefined) return;
    node.depth = depth;
    preOrder.push(node);
    node.children.forEach((child) => walk(child, depth + 1));
  };
  nodes.filter((n) => !n.parent).forEach((root) => walk(root, 0));
  // nodes left aside by a corrupted hierarchy are detached and kept as roots
  nodes.filter((n) => n.depth === undefined).forEach((node) => {
    if (node.depth !== undefined || !node.parent) return;
    node.parent.children = node.parent.children.filter((c) => c !== node);
    node.parent = null;
    walk(node, 0);
  });

  // 5. CUMULATED VALUES, FROM THE DEEPEST NODES (CHILDREN COME AFTER THEIR PARENT)
  [...preOrder].reverse().forEach((node) => {
    node.value = node.children.reduce((acc, child) => acc + child.value, node.ownValue);
  });

  // 6. ONE COLOR SCALE BY GROUP OF SIBLINGS, SO A LEVEL IS NEVER MONOCHROME
  const groups = [nodes.filter((n) => !n.parent), ...nodes.map((n) => n.children)];
  groups.filter((group) => group.length > 0).forEach((group) => {
    const max = d3.max(group.map((n) => n.value));
    const min = d3.min(group.map((n) => n.value));
    group.forEach((node) => {
      // a group of one (or of equal values) has no scale to spread on
      node.color = max === min ? colorScale(0.5, 0, 1) : colorScale(node.value, max, min);
    });
  });

  // 7. TREE ORDER, SIBLINGS SORTED BY VALUE
  const ordered = [];
  const sortAndPush = (node) => {
    ordered.push(node);
    [...node.children]
      .sort((a, b) => (b.value - a.value))
      .forEach((child) => sortAndPush(child));
  };
  nodes
    .filter((n) => !n.parent)
    .sort((a, b) => (b.value - a.value))
    .forEach((root) => sortAndPush(root));

  return ordered.map((node) => ({
    ...node.item,
    name: node.name,
    id: node.item.id || node.name,
    value: node.value,
    ownValue: node.ownValue,
    elementId: node.elementId,
    parentId: node.parent?.elementId || null,
    parentName: node.parent?.name || null,
    depth: node.depth,
    hasChildren: node.children.length > 0,
    color: getPrevColor(node.item.id || node.name) || node.color,
  }));
};

export default buildHierarchicalData;
